import { useTranslation } from 'react-i18next';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import addIcon from '@/assets/icons/add.svg';
import type { ClassificationDtoIn } from '@/models/in/ClassificationWithLimitsDtoIn';
import styles from './ClassificationAttachmentsSection.module.css';

type RequiredDocument = {
  file: File;
  requiredDocName: string;
};

type AdditionalDocument = {
  file: File | null;
  customDescription: string;
};

interface DocumentsSectionProps {
  classification?: ClassificationDtoIn;
  requiredDocuments: RequiredDocument[];
  additionalDocuments: AdditionalDocument[];
  onRequiredDocumentChange: (requiredDoc: string, file: File | null) => void;
  onAddAdditionalDocument: () => void;
  onRemoveAdditionalDocument: (docIndex: number) => void;
  onAdditionalDocumentChange: (docIndex: number, value: { file: File | null; customDescription: string }) => void;
  comments?: string;
  onCommentsChange?: (value: string) => void;
}

const ClassificationAttachmentsSection = ({
  classification,
  requiredDocuments,
  additionalDocuments,
  onRequiredDocumentChange,
  onAddAdditionalDocument,
  onRemoveAdditionalDocument,
  onAdditionalDocumentChange,
  comments,
  onCommentsChange,
}: DocumentsSectionProps) => {
  const { t } = useTranslation();

  if (!classification) {
    return null;
  }

  const hasRequiredDocuments = classification.requiredDocuments && classification.requiredDocuments.length > 0;

  return (
    <div className={styles.classificationGroup}>
      <div className={styles.classificationHeader}>
        <h5 className={styles.classificationName}>{t(classification.name)}</h5>
        {hasRequiredDocuments && <p className={styles.classificationNote}>יש להעלות לפחות אחד מהמסמכים הבאים:</p>}
      </div>

      <div className={styles.documentsList}>
        {/* Required documents */}
        {hasRequiredDocuments &&
          classification.requiredDocuments.map((requiredDoc) => {
            const fileForThisRequiredDoc = requiredDocuments.find((doc) => doc.requiredDocName === requiredDoc)?.file || null;

            return (
              <FileUpload
                key={requiredDoc}
                placeholder={t(`ClassificationsRequiredDocuments.${requiredDoc}`)}
                value={fileForThisRequiredDoc}
                onChange={(file) => onRequiredDocumentChange(requiredDoc, file)}
              />
            );
          })}

        {/* Additional documents */}
        {additionalDocuments.map((doc, docIndex) => {
          return (
            <FileUpload
              key={`additional-${docIndex}`}
              placeholder={t('documentName')}
              value={doc.file || null}
              onChange={(file) => onAdditionalDocumentChange(docIndex, { file, customDescription: doc.customDescription || '' })}
              customDescription={{
                description: doc.customDescription || '',
                onDescriptionChange: (description: string) => onAdditionalDocumentChange(docIndex, { file: doc.file, customDescription: description }),
                onRemove: () => onRemoveAdditionalDocument(docIndex),
              }}
            />
          );
        })}

        {/* Add additional document button */}
        <button
          onClick={onAddAdditionalDocument}
          className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
        >
          <img src={addIcon} alt="" />
          {t('addAdditionalDocument')}
        </button>

        {/* Comments textarea */}
        {onCommentsChange && (
          <div className={styles.commentsSection}>
            <label className="body-sm-semibold text-(--font-body-2)">{t('freeText')}</label>
            <textarea
              value={comments || ''}
              onChange={(e) => onCommentsChange(e.target.value)}
              placeholder={t('freeTextPlaceholder')}
              className={styles.commentsTextarea}
              rows={3}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassificationAttachmentsSection;
